import { useState, useCallback, useRef, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Prescription, PrescriptionDataTransformer, PrescriptionFormData } from "@/types/prescription";

export const usePrescriptionActions = (patientId: string, onDataChange: () => void) => {
  const { currentUser } = useAuth();

  // ✅ FIXED: Use refs for stable references to avoid stale closures
  const patientIdRef = useRef(patientId);
  const onDataChangeRef = useRef(onDataChange);
  const currentUserRef = useRef(currentUser);

  // Update refs when dependencies change
  useEffect(() => {
    patientIdRef.current = patientId;
    onDataChangeRef.current = onDataChange;
    currentUserRef.current = currentUser;
  }, [patientId, onDataChange, currentUser]);

  // Prescription management states
  const [showNewPrescription, setShowNewPrescription] = useState(false);
  const [viewingPrescription, setViewingPrescription] = useState<Prescription | null>(null);
  const [editingPrescription, setEditingPrescription] = useState<Prescription | null>(null);
  const [copyData, setCopyData] = useState<Partial<PrescriptionFormData> | null>(null);

  // ✅ FIXED: Cleanup function to reset all states
  const cleanup = useCallback(() => {
    setShowNewPrescription(false);
    setViewingPrescription(null);
    setEditingPrescription(null);
    setCopyData(null);
  }, []);

  // Handle new prescription
  const handleNewPrescription = useCallback(() => {
    setCopyData(null);
    setShowNewPrescription(true);
  }, []);

  // ✅ FIXED: Handle copy prescription with stable references
  const handleCopyPrescription = useCallback((prescription: Prescription) => {
    console.log("=== COPY PRESCRIPTION DEBUG ===");
    console.log("Original prescription:", prescription);
    
    // Use PrescriptionDataTransformer to properly convert prescription to form data
    const copyPrescriptionData = PrescriptionDataTransformer.toFormData(prescription, patientIdRef.current);
    
    // Override the date to today for the new prescription
    copyPrescriptionData.date = new Date().toISOString().split('T')[0];
    
    // Reset next visit for copied prescription
    copyPrescriptionData.next_visit = { date: "", time: "", notes: "" };
    
    // Update doctor and clinic info if available
    if (currentUserRef.current?.doctor_id) {
      copyPrescriptionData.doctor_id = currentUserRef.current.doctor_id;
    }
    if (currentUserRef.current?.clinic_id) {
      copyPrescriptionData.clinic_id = currentUserRef.current.clinic_id;
    }
    
    console.log("Generated copy data (transformed):", copyPrescriptionData);
    console.log("=== END COPY DEBUG ===");
    
    setCopyData(copyPrescriptionData);
    setShowNewPrescription(true);
  }, []); // ✅ FIXED: No dependencies needed since we use refs

  // ✅ FIXED: Handle prescription saved with stable reference
  const handlePrescriptionSaved = useCallback(() => {
    onDataChangeRef.current(); // Use ref to avoid stale closure
    setShowNewPrescription(false);
    setCopyData(null);
  }, []); // ✅ FIXED: No dependencies needed since we use refs

  // ✅ FIXED: Handle prescription cancel with cleanup
  const handlePrescriptionCancel = useCallback(() => {
    setShowNewPrescription(false);
    setCopyData(null);
  }, []);

  // ✅ FIXED: Handle edit prescription with validation
  const handleEditPrescription = useCallback((prescription: Prescription) => {
    if (!patientIdRef.current) {
      console.warn('No patient ID available for editing prescription');
      return;
    }
    
    setEditingPrescription({
      ...prescription,
      patient_id: prescription.patient_id || patientIdRef.current
    });
  }, []); // ✅ FIXED: No dependencies needed since we use refs

  // ✅ FIXED: Handle edit saved with stable reference
  const handleEditSaved = useCallback(() => {
    onDataChangeRef.current(); // Use ref to avoid stale closure
    setEditingPrescription(null);
  }, []); // ✅ FIXED: No dependencies needed since we use refs

  // Handle edit cancel
  const handleEditCancel = useCallback(() => {
    setEditingPrescription(null);
  }, []);

  // Handle view prescription
  const handleViewPrescription = useCallback((prescription: Prescription) => {
    setViewingPrescription(prescription);
  }, []);

  // Handle close view
  const handleCloseView = useCallback(() => {
    setViewingPrescription(null);
  }, []);

  // ✅ FIXED: Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup when component unmounts
      cleanup();
    };
  }, [cleanup]);

  return {
    // State
    showNewPrescription,
    viewingPrescription,
    editingPrescription,
    copyData,

    // Actions
    handleNewPrescription,
    handleCopyPrescription,
    handlePrescriptionSaved,
    handlePrescriptionCancel,
    handleEditPrescription,
    handleEditSaved,
    handleEditCancel,
    handleViewPrescription,
    handleCloseView,

    // Setters (for direct state manipulation if needed)
    setShowNewPrescription,
    setViewingPrescription,
    setEditingPrescription,
    setCopyData,

    // ✅ FIXED: Add cleanup function for external use
    cleanup
  };
}; 